// Подредбата на лентата под масите — личната настройка `footerActions` (org
// базата). Записът е ЦЕЛИЯТ ред на лентата: основните бутони (ключовете по-долу)
// и изнесените от „Опции" (ключовете на действията от TableFooter), така че
// изнесен бутон може да стои и пред, и между основните. Чисти функции —
// свиването е в `useFooterDensity.ts`, влаченето в `useFooterDrag.ts`.

import type { FooterButtonProps } from "../FooterButton";

/** Бутон в лентата — основен или изнесен от „Опции" — с ключа си в записа. */
export interface FooterBarItem {
  key: string;
  button: FooterButtonProps;
}

/** Двете зони за пускане (dnd-kit droppable id-та): цялата лента и панелът „Опции". */
export const FOOTER_ZONE_ID = "footer-zone";
export const OPTIONS_ZONE_ID = "options-zone";

/** Откъде тръгва влаченето — от лентата или от „Опции". */
export type FooterDragSource = "footer" | "options";

/** `data` на всеки draggable бутон — по нея `useFooterDrag` познава кой е. */
export interface FooterDragData {
  actionKey: string;
  source: FooterDragSource;
}

/** Ключът на бутона „Опции" в записа. */
export const OPTIONS_KEY = "options";

/** Основните бутони в обичайния им ред. И те се влачат (само в лентата —
 *  подреждат се, но не се крият в „Опции"). Не ги преименувай: ключовете са
 *  в записа. */
export const MAIN_FOOTER_KEYS = [
  "newTable",
  "addToTable",
  "closeTable",
  OPTIONS_KEY,
] as const;

export const isMainKey = (key: string) =>
  (MAIN_FOOTER_KEYS as readonly string[]).includes(key);

/** Изнесените в лентата (записани и налични по права) и останалите в „Опции". */
export const splitFooterActions = <Action extends { key: string }>(
  actions: Action[],
  storedKeys: readonly string[],
): { pinned: Action[]; inOptions: Action[] } => {
  const stored = new Set(storedKeys);
  return {
    pinned: actions.filter((action) => stored.has(action.key)),
    inOptions: actions.filter((action) => !stored.has(action.key)),
  };
};

/**
 * Редът на лентата: записаният, само с наличните бутони. Основен бутон, който
 * липсва в записа (правото е дадено след последната подредба), застава след
 * предишния основен от обичайния ред, иначе преди следващия. Запис без нито
 * един основен (нищо не е подреждано) → основните в обичайния ред, а
 * изнесените — преди „Опции".
 */
export const buildFooterOrder = (
  storedKeys: readonly string[],
  mainKeys: string[],
  pinnedKeys: string[],
): string[] => {
  const available = new Set([...mainKeys, ...pinnedKeys]);
  const order = [...new Set(storedKeys)].filter((key) => available.has(key));

  if (!order.some(isMainKey)) {
    const beforeOptions = mainKeys.filter((key) => key !== OPTIONS_KEY);
    const optionsTail = mainKeys.includes(OPTIONS_KEY) ? [OPTIONS_KEY] : [];
    return [...beforeOptions, ...order, ...optionsTail];
  }

  mainKeys.forEach((key, index) => {
    if (order.includes(key)) return;
    const previous = mainKeys
      .slice(0, index)
      .reverse()
      .find((candidate) => order.includes(candidate));
    if (previous) {
      order.splice(order.indexOf(previous) + 1, 0, key);
      return;
    }
    const next = mainKeys
      .slice(index + 1)
      .find((candidate) => order.includes(candidate));
    order.splice(next ? order.indexOf(next) : order.length, 0, key);
  });
  return order;
};

/**
 * Новият запис след влачене: редът на лентата, после изнесените, които сега
 * не са налични (без право) — да се върнат, ако правото се върне. Основен
 * бутон без право НЕ се пази: при връщане на правото застава на обичайното
 * си място (`buildFooterOrder`). `removedKey` е бутонът, върнат в „Опции".
 */
export const nextStoredFooterActions = (
  storedKeys: readonly string[],
  orderAfter: string[],
  removedKey?: string,
): string[] => [
  ...orderAfter,
  ...storedKeys.filter(
    (key) =>
      !orderAfter.includes(key) && key !== removedKey && !isMainKey(key),
  ),
];

/**
 * Къде в лентата ще падне влаченият бутон: броят бутони, чийто център е
 * вляво от показалеца. Центровете са от ПРЕДИ влаченето — иначе мястото за
 * пускане, което избутва съседите, би сменяло и самото изчисление (трептене).
 */
export const dropIndexFromCenters = (
  centers: number[],
  pointerX: number,
): number => centers.filter((center) => center < pointerX).length;

/** `keys` с `key`, вмъкнат на `index` (преди това махнат, ако вече е вътре). */
export const insertKeyAt = (
  keys: string[],
  key: string,
  index: number,
): string[] => {
  const withoutKey = keys.filter((existing) => existing !== key);
  const safeIndex = Math.max(0, Math.min(index, withoutKey.length));
  return [
    ...withoutKey.slice(0, safeIndex),
    key,
    ...withoutKey.slice(safeIndex),
  ];
};
